/**
 * AbwesenheitenListePage — Universal HR-Liste für alle Abwesenheiten (#1010 BR-22)
 *
 * Listet ALLE Abwesenheiten (urlaub_antraege + abwesenheiten + krankenstaende-via-abwesenheiten)
 * mit Filter nach MA/Jahr/Art/Status und Aktionen Edit/Stornieren/Löschen.
 *
 * @page /abwesenheiten/liste
 * @roles administrator, ops_manager, teamleiter
 * @since v2.441.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit2, Ban, Trash2, X, Search, AlertTriangle, RefreshCw, Filter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SharedAbwesenheitenTabs from '../components/SharedAbwesenheitenTabs';
import { useDialog } from '../hooks/useDialog';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPatch = (path, body) => api(path, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiDelete = (path) => api(path, { method: 'DELETE' });

const fmtDate = (s) => {
  if (!s) return '—';
  const str = typeof s === 'string' ? s.slice(0, 10) : new Date(s).toISOString().slice(0, 10);
  const [y, m, d] = str.split('-');
  return `${d}.${m}.${y}`;
};

// Storno-Modal: Grund + Kommentar
function StornoModal({ open, onClose, onConfirm, t, eintrag }) {
  const [grund, setGrund] = useState('');
  const [kommentar, setKommentar] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setGrund(''); setKommentar(''); } }, [open]);
  if (!open || !eintrag) return null;

  const handleSubmit = async () => {
    if (!grund.trim()) return;
    setSaving(true);
    try { await onConfirm({ grund: grund.trim(), kommentar: kommentar.trim() || null }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('abwesenheitenListe.stornoTitle', 'Abwesenheit stornieren')}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-sm space-y-1">
            <div><span className="text-[var(--text-tertiary)]">{t('nav.employees', 'Mitarbeiter')}: </span><span className="font-medium text-[var(--text-primary)]">{eintrag.ma_name}</span></div>
            <div><span className="text-[var(--text-tertiary)]">{t('abwesenheitenListe.art', 'Art')}: </span><span className="text-[var(--text-primary)]">{eintrag.art_label}</span></div>
            <div><span className="text-[var(--text-tertiary)]">{t('urlaub.vonDatum', 'Von')}: </span><span className="text-[var(--text-primary)]">{fmtDate(eintrag.von_datum)} – {fmtDate(eintrag.bis_datum || eintrag.von_datum)}</span></div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.stornoGrund', 'Grund')} *</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]" value={grund} onChange={e => setGrund(e.target.value)} autoFocus>
              <option value="">{t('common.select', '— Auswählen —')}</option>
              <option value="falsch_erfasst">{t('abwesenheitenListe.stornoGrund.falsch', 'Versehentlich falsch erfasst')}</option>
              <option value="datum_geaendert">{t('abwesenheitenListe.stornoGrund.datumGeaendert', 'Datum hat sich geändert')}</option>
              <option value="ma_korrektur">{t('abwesenheitenListe.stornoGrund.maKorrektur', 'MA-Korrektur (z.B. früher zurück)')}</option>
              <option value="duplikat">{t('abwesenheitenListe.stornoGrund.duplikat', 'Doppeleintrag')}</option>
              <option value="sonstiges">{t('abwesenheitenListe.stornoGrund.sonstiges', 'Sonstiges')}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.stornoKommentar', 'Kommentar (optional)')}</label>
            <textarea
              className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              rows={3}
              value={kommentar}
              onChange={e => setKommentar(e.target.value)}
              placeholder={t('abwesenheitenListe.stornoKommentar.placeholder', 'Kontext für Audit-Trail …')}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={handleSubmit} disabled={saving || !grund.trim()}
            className="px-4 py-2 text-sm rounded-[10px] bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50 transition-colors">
            {saving ? '...' : <span className="inline-flex items-center gap-1.5"><Ban size={14} /> {t('urlaub.stornieren', 'Stornieren')}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit-Modal: Datum + Tage + Notiz
function EditModal({ open, onClose, onConfirm, t, eintrag, abwesenheitsarten, lang }) {
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [tage, setTage] = useState('');
  const [artKey, setArtKey] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !eintrag) return;
    setVon(eintrag.von_datum?.slice(0, 10) || '');
    setBis((eintrag.bis_datum || eintrag.von_datum)?.slice(0, 10) || '');
    setTage(eintrag.tage || '');
    setArtKey(eintrag.art_key || '');
    setNotiz(eintrag.grund || eintrag.notiz_ma || eintrag.notiz_hr || '');
    setError(null);
  }, [open, eintrag]);

  if (!open || !eintrag) return null;

  const handleSubmit = async () => {
    if (!von) { setError(t('abwesenheitenListe.edit.fehlerVon', 'Von-Datum erforderlich')); return; }
    setSaving(true); setError(null);
    try {
      await onConfirm({
        art_key: artKey,
        von_datum: von,
        bis_datum: bis || von,
        tage: tage ? parseFloat(tage) : null,
        notiz: notiz || null,
      });
    } catch (err) {
      setError(err?.error || err?.message || String(err));
    } finally { setSaving(false); }
  };

  // urlaub_antraege Quelle erlaubt nur Standard-Urlaub-Typen, abwesenheiten alle Arten
  const isUrlaubQuelle = eintrag.quelle === 'urlaub_antraege';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('abwesenheitenListe.editTitle', 'Abwesenheit bearbeiten')}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-sm">
            <div><span className="text-[var(--text-tertiary)]">{t('nav.employees', 'Mitarbeiter')}: </span><span className="font-medium text-[var(--text-primary)]">{eintrag.ma_name}</span></div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.art', 'Art')}</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={artKey} onChange={e => setArtKey(e.target.value)} disabled={isUrlaubQuelle}>
              {(isUrlaubQuelle
                ? abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege')
                // #1701: Gleitzeittag im Edit nur anbieten, wenn der Eintrag bereits einer ist (Backend-Guard gespiegelt)
                : abwesenheitsarten.filter(a => a.quelle === 'abwesenheiten' && (a.key !== 'gleitzeittag' || eintrag.art_key === 'gleitzeittag'))
              ).map(a => <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.vonDatum', 'Von')}</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={von} onChange={e => setVon(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.bisDatum', 'Bis')}</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={bis} onChange={e => setBis(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.tage', 'Tage')}</label>
            <input type="number" step="0.5" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={tage} onChange={e => setTage(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.notizHr', 'Notiz')}</label>
            <textarea className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              rows={2} value={notiz} onChange={e => setNotiz(e.target.value)} />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold disabled:opacity-50 transition-colors">
            {saving ? '...' : t('common.save', 'Speichern')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AbwesenheitenListePage() {
  const { t, language: lang } = useLanguage();
  const { user } = useAuth();
  const dialog = useDialog();

  const isAdmin = ['administrator', 'ops_manager'].includes(user?.rolle);
  const canHardDelete = user?.rolle === 'administrator';

  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [abwesenheitsarten, setAbwesenheitsarten] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter
  const [maFilter, setMaFilter] = useState('');
  const [artFilter, setArtFilter] = useState('');
  const [jahrFilter, setJahrFilter] = useState(new Date().getFullYear());
  const [inklStorniert, setInklStorniert] = useState(false);
  const [search, setSearch] = useState('');

  // Modals
  const [stornoEintrag, setStornoEintrag] = useState(null);
  const [editEintrag, setEditEintrag] = useState(null);

  // Initial-Load
  useEffect(() => {
    api('/api/mitarbeiter?aktiv_nur=false').then(d => setMitarbeiter(d.data || d || [])).catch(() => {});
    api('/api/abwesenheitsarten?alle=true').then(d => setAbwesenheitsarten(d.data || [])).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (maFilter) params.append('mitarbeiter_id', maFilter);
      if (jahrFilter) {
        params.append('von', `${jahrFilter}-01-01`);
        params.append('bis', `${jahrFilter}-12-31`);
      }
      if (inklStorniert) params.append('inkl_storniert', 'true');

      const [abwResp, urlaubResp] = await Promise.all([
        api(`/api/abwesenheiten?${params.toString()}`).catch(() => ({ data: [] })),
        api(`/api/urlaub/antraege${maFilter ? `?ma_id=${maFilter}` : ''}${jahrFilter ? `${maFilter ? '&' : '?'}jahr=${jahrFilter}` : ''}`).catch(() => ({ data: [] })),
      ]);

      // Normalisiere
      const abwRows = (abwResp.data || []).map(a => ({
        id: a.id,
        quelle: 'abwesenheiten',
        art_key: a.art_key,
        art_label: lang === 'de' ? a.label_de : (a.label_en || a.label_de),
        art_farbe: a.farbe,
        ma_id: a.mitarbeiter_id,
        ma_name: a.ma_name,
        von_datum: a.von_datum,
        bis_datum: a.bis_datum,
        von_zeit: a.von_zeit,
        bis_zeit: a.bis_zeit,
        tage: a.tage,
        stunden: a.stunden,
        status: a.status,
        grund: a.grund,
        storniert_am: a.storniert_am,
        storno_grund: a.storno_grund,
        storno_kommentar: a.storno_kommentar,
        storno_user_name: a.storno_user_vorname ? `${a.storno_user_vorname} ${a.storno_user_nachname}` : null,
      }));

      // urlaub_antraege: Filter Status (nicht-storniert oder ALLE bei inklStorniert)
      const filteredUrlaub = (urlaubResp.data || []).filter(u => {
        if (artFilter) {
          const matched = abwesenheitsarten.find(a => a.key === u.typ);
          if (!matched || matched.key !== artFilter) return false;
        }
        if (!inklStorniert && u.status === 'storniert') return false;
        return true;
      });
      const urlaubRows = filteredUrlaub.map(u => {
        const art = abwesenheitsarten.find(a => a.key === u.typ);
        return {
          id: u.id,
          quelle: 'urlaub_antraege',
          art_key: u.typ,
          art_label: art ? (lang === 'de' ? art.label_de : (art.label_en || art.label_de)) : u.typ,
          art_farbe: art?.farbe || '#6B7280',
          ma_id: u.mitarbeiter_id,
          ma_name: u.ma_name,
          von_datum: u.von_datum,
          bis_datum: u.bis_datum,
          tage: u.tage,
          stunden: null,
          status: u.status,
          grund: u.notiz_ma || u.notiz_hr,
          storniert_am: u.storniert_am,
          storno_grund: u.storniert_grund,
          storno_kommentar: u.storno_kommentar,
        };
      });

      // #1213 Bug #4: Urlaub liegt teils doppelt vor — einmal in abwesenheiten
      // (quelle='historisch_import') und einmal in urlaub_antraege. urlaub_antraege
      // ist die SSoT; Mirror-Dupes aus abwesenheiten mit gleichem MA/Art/Zeitraum raus.
      const dedupKey = (r) => `${r.ma_id}|${r.art_key}|${String(r.von_datum).slice(0, 10)}|${String(r.bis_datum).slice(0, 10)}`;
      const urlaubKeys = new Set(urlaubRows.map(dedupKey));
      const dedupedAbw = abwRows.filter(r => !urlaubKeys.has(dedupKey(r)));

      // Filter by art (für abwesenheiten)
      const merged = [...dedupedAbw, ...urlaubRows].filter(r => {
        if (artFilter && r.art_key !== artFilter) return false;
        return true;
      });

      // Sort by von_datum DESC
      merged.sort((a, b) => (a.von_datum < b.von_datum ? 1 : a.von_datum > b.von_datum ? -1 : 0));
      setRows(merged);
    } finally { setLoading(false); }
  }, [maFilter, artFilter, jahrFilter, inklStorniert, lang, abwesenheitsarten]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r =>
      (r.ma_name || '').toLowerCase().includes(q) ||
      (r.art_label || '').toLowerCase().includes(q) ||
      (r.grund || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    const aktiv = rows.filter(r => r.status !== 'storniert' && r.status !== 'abgelehnt').length;
    const storniert = rows.filter(r => r.status === 'storniert').length;
    return { aktiv, storniert, gesamt: rows.length };
  }, [rows]);

  // ─── Aktionen ───
  const handleStorno = async ({ grund, kommentar }) => {
    const e = stornoEintrag;
    try {
      if (e.quelle === 'abwesenheiten') {
        await apiPatch(`/api/abwesenheiten/${e.id}/stornieren`, { grund, kommentar });
      } else if (e.quelle === 'urlaub_antraege') {
        await apiPut(`/api/urlaub/antraege/${e.id}/stornieren`, { grund, kommentar });
      }
      setStornoEintrag(null);
      reload();
    } catch (err) {
      await dialog.alert(t('common.error', 'Fehler'), err?.error || err?.message || String(err), { variant: 'danger' });
    }
  };

  const handleEdit = async (data) => {
    const e = editEintrag;
    try {
      if (e.quelle === 'abwesenheiten') {
        const body = {
          art_key: data.art_key,
          von_datum: data.von_datum,
          bis_datum: data.bis_datum,
          tage: data.tage,
          grund: data.notiz,
        };
        await apiPut(`/api/abwesenheiten/${e.id}`, body);
      } else if (e.quelle === 'urlaub_antraege') {
        const body = {
          von_datum: data.von_datum,
          bis_datum: data.bis_datum,
          tage: data.tage,
          notiz_hr: data.notiz,
        };
        await apiPut(`/api/urlaub/antraege/${e.id}/edit`, body);
      }
      setEditEintrag(null);
      reload();
    } catch (err) {
      throw err;
    }
  };

  const handleHardDelete = async (eintrag) => {
    const ok = await dialog.confirm(
      t('abwesenheitenListe.hardDeleteTitle', 'Endgültig löschen?'),
      t('abwesenheitenListe.hardDeleteMessage', 'Dieser Eintrag wird unwiderruflich gelöscht. Audit-Trail geht verloren. Stattdessen lieber stornieren?'),
      { variant: 'danger', confirmLabel: t('abwesenheitenListe.endgueltigLoeschen', 'Endgültig löschen') }
    );
    if (!ok) return;
    try {
      if (eintrag.quelle === 'abwesenheiten') {
        await apiDelete(`/api/abwesenheiten/${eintrag.id}/hard`);
      } else if (eintrag.quelle === 'urlaub_antraege') {
        await apiDelete(`/api/urlaub/antraege/${eintrag.id}/hard`);
      }
      reload();
    } catch (err) {
      await dialog.alert(t('common.error', 'Fehler'), err?.error || err?.message || String(err), { variant: 'danger' });
    }
  };

  const isStorniert = (r) => r.status === 'storniert' || r.status === 'abgelehnt';

  return (
    <div className="p-6 max-w-full">
      <SharedAbwesenheitenTabs />

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary,#1A1A2E)]">{t('abwesenheitenListe.title', 'Alle Abwesenheiten')}</h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('abwesenheitenListe.subtitle', 'Bearbeiten · Stornieren · Löschen aller Abwesenheitsarten')}</p>
        </div>
        <button onClick={reload} className="px-3 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] inline-flex items-center gap-1.5">
          <RefreshCw size={14} /> {t('common.reload', 'Aktualisieren')}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.aktiv', 'Aktiv')}</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{counts.aktiv}</p>
        </div>
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.storniert', 'Storniert')}</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{counts.storniert}</p>
        </div>
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.gesamt', 'Gesamt')}</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{counts.gesamt}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)] mb-5">
        <div className="flex items-center gap-3 mb-3">
          <Filter size={14} className="text-[var(--text-tertiary)]" />
          <span className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('abwesenheitenListe.filter', 'Filter')}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('nav.employees', 'Mitarbeiter')}</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={maFilter} onChange={e => setMaFilter(e.target.value)}>
              <option value="">{t('abwesenheitenListe.alleMa', 'Alle')}</option>
              {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.art', 'Art')}</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={artFilter} onChange={e => setArtFilter(e.target.value)}>
              <option value="">{t('abwesenheitenListe.alleArt', 'Alle')}</option>
              {abwesenheitsarten.map(a => <option key={a.key} value={a.key}>{lang === 'de' ? a.label_de : (a.label_en || a.label_de)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.jahr', 'Jahr')}</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={jahrFilter} onChange={e => setJahrFilter(parseInt(e.target.value))}>
              {/* #1850: dynamisch 2024 … Folgejahr statt hardcodierter Liste */}
              {Array.from({ length: new Date().getFullYear() + 2 - 2024 }, (_, i) => 2024 + i).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheitenListe.suche', 'Suche')}</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <input type="text" className="w-full pl-8 pr-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                placeholder={t('abwesenheitenListe.suchePlaceholder', 'Name, Art, Notiz …')}
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">&nbsp;</label>
            <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer pt-1.5">
              <input type="checkbox" className="rounded border-[var(--border-default)]"
                checked={inklStorniert} onChange={e => setInklStorniert(e.target.checked)} />
              {t('abwesenheitenListe.inklStorniert', 'Inkl. stornierte')}
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
              <tr>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('nav.employees', 'Mitarbeiter')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('abwesenheitenListe.art', 'Art')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('abwesenheitenListe.zeitraum', 'Zeitraum')}</th>
                <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.tage', 'Tage')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('abwesenheitenListe.notiz', 'Notiz')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">Status</th>
                <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('abwesenheitenListe.aktionen', 'Aktionen')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-[var(--text-tertiary)]">{t('common.loading', 'Lädt …')}</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-[var(--text-tertiary)]">{t('abwesenheitenListe.empty', 'Keine Abwesenheiten gefunden')}</td></tr>
              )}
              {!loading && filtered.map(r => {
                const stl = isStorniert(r);
                return (
                  <tr key={`${r.quelle}-${r.id}`} className={`border-b border-[var(--border-default,#E5E7EB)] last:border-0 hover:bg-[var(--bg-card-hover,#FAFAFE)] ${stl ? 'opacity-60' : ''}`}>
                    <td className={`px-4 py-3 ${stl ? 'line-through' : ''} text-[var(--text-primary)] font-medium`}>{r.ma_name}</td>
                    <td className={`px-4 py-3 ${stl ? 'line-through' : ''}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.art_farbe || '#6B7280' }}></span>
                        <span className="text-[var(--text-primary)]">{r.art_label}</span>
                      </span>
                    </td>
                    <td className={`px-4 py-3 ${stl ? 'line-through' : ''} text-[var(--text-secondary)]`}>
                      {fmtDate(r.von_datum)}
                      {r.bis_datum && r.bis_datum !== r.von_datum && ` – ${fmtDate(r.bis_datum)}`}
                      {r.von_zeit && r.bis_zeit && (
                        <span className="ml-2 text-xs text-[var(--text-tertiary)]">{r.von_zeit?.slice(0, 5)}–{r.bis_zeit?.slice(0, 5)}</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right ${stl ? 'line-through' : ''} text-[var(--text-primary)] tabular-nums`}>
                      {r.tage ? parseFloat(r.tage).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) : (r.stunden ? `${parseFloat(r.stunden)}h` : '—')}
                    </td>
                    <td className={`px-4 py-3 text-[var(--text-tertiary)] truncate max-w-xs ${stl ? 'line-through' : ''}`} title={r.grund || ''}>{r.grund || '—'}</td>
                    <td className="px-4 py-3">
                      {stl ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-[var(--bg-inset,#F8F9FC)] border-[var(--border-default,#E5E7EB)] text-[var(--text-tertiary,#6B7280)]" title={r.storno_grund || ''}>
                          <Ban size={12} /> {t('abwesenheitenListe.storniert', 'Storniert')}
                        </span>
                      ) : r.status === 'beantragt' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-amber-50 border-amber-200 text-amber-800">
                          {t('abwesenheitenListe.beantragt', 'Beantragt')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border bg-green-50 border-green-200 text-green-800">
                          {t('abwesenheitenListe.aktiv', 'Aktiv')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!stl && (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setEditEintrag(r)} title={t('abwesenheitenListe.bearbeiten', 'Bearbeiten')}
                            className="p-1.5 rounded-md hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => setStornoEintrag(r)} title={t('urlaub.stornieren', 'Stornieren')}
                            className="p-1.5 rounded-md hover:bg-red-50 text-red-600">
                            <Ban size={14} />
                          </button>
                          {canHardDelete && (
                            <button onClick={() => handleHardDelete(r)} title={t('abwesenheitenListe.endgueltigLoeschen', 'Endgültig löschen')}
                              className="p-1.5 rounded-md hover:bg-red-50 text-red-700">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                      {stl && canHardDelete && (
                        <button onClick={() => handleHardDelete(r)} title={t('abwesenheitenListe.endgueltigLoeschen', 'Endgültig löschen')}
                          className="p-1.5 rounded-md hover:bg-red-50 text-red-700">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StornoModal open={!!stornoEintrag} onClose={() => setStornoEintrag(null)} onConfirm={handleStorno} t={t} eintrag={stornoEintrag} />
      <EditModal open={!!editEintrag} onClose={() => setEditEintrag(null)} onConfirm={handleEdit} t={t} eintrag={editEintrag} abwesenheitsarten={abwesenheitsarten} lang={lang} />
      {dialog.element}
    </div>
  );
}
